import React, {
  forwardRef,
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
} from 'react';
import { Badge, Button, Empty, Table, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { IconButton } from '../../common/controls/Controls';
import { QuestionModal } from '../../common/';
import { EditOutlined, CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import { isArray, isNull, isUndefined } from 'lodash';
import dayjs from 'dayjs';
import useIsUserAdmin from '../../../hooks/useIsUserAdmin';
import checkPermission from '../../../utils/helpers/checkPermission';
import DataGridOverviewRowButton from './DataGridOverviewRowButton';
import DataGridActiveFilters from './DataGridActiveFilters';
import {
  updateTableState,
  resetTableState,
} from '../../../redux/slices/localStorageSlice';
import { TABLE_STATES_CACHE } from '../../../utils/constants/cache_constants.js';
import { DATETIME_FORMAT_DATE } from '../../../utils/helpers/CommonHelper.jsx';
import './DataGrid.sass';

/**
 * AntD passes filters under column.key (if set), otherwise under dataIndex.
 */
const getColumnKey = (col) =>
  col.key ?? (isArray(col.dataIndex) ? col.dataIndex.join('.') : col.dataIndex);

/**
 * Column whose filter state is owned by the parent (server-side filters).
 */
const isExternallyFilteredColumn = (col) =>
  typeof col.onFilterClear === 'function';

const hasFilterValue = (value) =>
  value !== undefined &&
  value !== null &&
  value !== '' &&
  !(Array.isArray(value) && value.length === 0);

// dayjs values persisted in localStorage are serialized to ISO datetime strings.
const ISO_DATETIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;

const getColumnFilterTitle = (col, key) => {
  if (col.filterTitle) return col.filterTitle;
  if (typeof col.title === 'string') return col.title;
  if (typeof col.title?.props?.children === 'string') {
    return col.title.props.children;
  }
  return key;
};

const formatSingleFilterValue = (value, t) => {
  if (dayjs.isDayjs(value)) return value.format(DATETIME_FORMAT_DATE);
  if (typeof value === 'string' && ISO_DATETIME_REGEX.test(value)) {
    return dayjs(value).format(DATETIME_FORMAT_DATE);
  }
  if (typeof value === 'boolean') return value ? t('Áno') : t('Nie');
  return String(value);
};

const formatFilterValue = (col, value, t) => {
  if (typeof col.filterValueFormatter === 'function') {
    return col.filterValueFormatter(value);
  }

  const option = Array.isArray(col.filters)
    ? col.filters.find((f) => String(f.value) === String(value))
    : null;
  if (option) return option.text;

  if (Array.isArray(value)) {
    return value.map((v) => formatSingleFilterValue(v, t)).join(' – ');
  }

  return formatSingleFilterValue(value, t);
};

const filterCountLabel = (count, t) => {
  if (count === 1) return t('{{count}} filter', { count });
  if (count >= 2 && count <= 4) return t('{{count}} filtre', { count });
  return t('{{count}} filtrov', { count });
};

const joinFilterLabels = (labels) =>
  labels.every((label) => ['string', 'number'].includes(typeof label))
    ? labels.join(', ')
    : labels.map((label, index) => (
        <React.Fragment key={index}>
          {index > 0 ? ', ' : null}
          {label}
        </React.Fragment>
      ));

// https://ant.design/components/table/
const DataGrid = forwardRef(function DataGrid(
  {
    allowClick,
    columns,
    customSettings,
    customSettingsPosition = 'before',
    dataSource, // antd prop
    disableSettings = false,
    showActionText = true,
    secondaryCondition = () => true,
    deleteCondition = () => true,
    editDisabledTooltip,
    deleteDisabledTooltip,
    deleteIcon,
    deleteTooltip,
    deleteModalTitle,
    editIcon,
    editTooltip,
    disableExpandRow = () => true,
    onDelete,
    onEditClick,
    onPreviewClick,
    previewTooltip,
    onRowClick,
    onRowDetailClick, // fallback row click (e.g. open detail) when onRowClick is not available for the user
    rowClickOpensPreview = false, // fallback row click opens onPreviewClick (requires the show permission)
    optionsWidth,
    permissionsBasic,
    resetFiltersCallback = null,
    uniqueTableId, // unique id for table state, define if duplicate permissionsBasic exists
    authorKey,
    openDeleteModal = true,
    pageSize = 10,
    seriousDeleteQuestion = null,
    customExpandedRowRender,
    defaultExpandedRowKeys, // NEW: allows parent to control which rows are expanded initially
    expandedRowKeys, // NEW controlled expanded rows
    onExpandedRowsChange, // NEW callback when expanded rows change
    rowSelection,
    expandIcon,
    onChange: onTableChange,
    ...restProps
  },
  ref,
) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const user = useSelector((state) => state.auth.user);
  const isAdmin = useIsUserAdmin();

  const tableId = uniqueTableId || permissionsBasic;
  const dispatch = useDispatch();

  // Use this tableId for state management
  const rawTableState = useSelector(
    (state) => state.localStorage.tableStates[tableId],
  );

  const tableState = useMemo(() => {
    return (
      rawTableState || {
        filters: {},
        sorter: {},
        pagination: { current: 1, pageSize },
      }
    );
  }, [rawTableState, pageSize]);

  // permissions
  const [rowToBeDeleted, setRowToBeDeleted] = useState(null);

  // calculate the width of settings/actions/options column
  const [columnWidth, setColumnWidth] = useState(
    typeof optionsWidth === 'number' ? optionsWidth : null,
  );
  const columnRef = useRef(null);
  const tableWrapperRef = useRef(null);
  useEffect(() => {
    // If consumer provided explicit width, honor it and skip auto-measurement
    if (typeof optionsWidth === 'number') {
      setColumnWidth(optionsWidth);
      return;
    }

    if (columnRef.current) {
      const buttons = columnRef.current.querySelectorAll('button');
      let totalWidth = 0;
      buttons.forEach((button) => {
        totalWidth += button.offsetWidth;
      });
      totalWidth += 60;
      if (totalWidth < 110) totalWidth = 110;
      setColumnWidth(totalWidth);
    }
  }, [onEditClick, onDelete, onPreviewClick, showActionText, optionsWidth]);

  const canDelete = useMemo(
    () => checkPermission(user, `${permissionsBasic}.destroy`, isAdmin),
    [permissionsBasic, user, isAdmin],
  );

  const canUpdate = useMemo(
    () => checkPermission(user, `${permissionsBasic}.update`, isAdmin),
    [permissionsBasic, user, isAdmin],
  );

  const canShow = useMemo(
    () => checkPermission(user, `${permissionsBasic}.show`, isAdmin),
    [permissionsBasic, user, isAdmin],
  );

  const canUseRowClick =
    typeof onRowClick === 'function' &&
    (canUpdate || isUndefined(permissionsBasic));
  const canUsePreviewRowClick =
    rowClickOpensPreview && canShow && typeof onPreviewClick === 'function';
  const isRowClickable =
    canUseRowClick ||
    typeof onRowDetailClick === 'function' ||
    canUsePreviewRowClick;

  // local filters when TABLE_STATES_CACHE is off
  const [filters, setFilters] = useState({});

  const activeFilters = TABLE_STATES_CACHE ? tableState.filters : filters;

  const resetAllFilters = useCallback(() => {
    if (resetFiltersCallback) {
      resetFiltersCallback();
    } else if (TABLE_STATES_CACHE) {
      dispatch(resetTableState({ tableId }));
    } else {
      setFilters({});
    }
  }, [resetFiltersCallback, dispatch, tableId]);

  const clearColumnFilter = useCallback(
    (key) => {
      if (TABLE_STATES_CACHE) {
        dispatch(
          updateTableState({
            tableId,
            filters: { ...tableState.filters, [key]: null },
            sorter: tableState.sorter,
            pagination: { ...tableState.pagination, current: 1 },
          }),
        );
      } else {
        setFilters((prev) => ({ ...prev, [key]: null }));
      }
    },
    [dispatch, tableId, tableState],
  );

  const activeFilterItems = useMemo(
    () =>
      columns.flatMap((col) => {
        const key = getColumnKey(col);
        if (!key) return [];

        const isExternal = isExternallyFilteredColumn(col);
        const rawValue = isExternal ? col.filteredValue : activeFilters?.[key];
        const values = (Array.isArray(rawValue) ? rawValue : [rawValue]).filter(
          hasFilterValue,
        );
        if (values.length === 0) return [];

        return [
          {
            key,
            title: getColumnFilterTitle(col, key),
            label: joinFilterLabels(
              values.map((v) => formatFilterValue(col, v, t)),
            ),
            onClear: isExternal
              ? col.onFilterClear
              : () => clearColumnFilter(key),
          },
        ];
      }),
    [columns, activeFilters, clearColumnFilter, t],
  );

  const activeFilterCount = activeFilterItems.length;

  // Columns configuration + actions column
  const columnsWithSettings = useMemo(() => {
    const decoratedColumns = [...columns];

    if (!disableSettings) {
      decoratedColumns.push({
        title: (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              minWidth: 110,
            }}
          >
            <span>{showActionText && t('Akcie')}</span>
            <Badge
              count={activeFilterCount}
              size="small"
              offset={[-4, 4]}
              color={token.colorWarning}
              title=""
            >
              <IconButton
                onClick={resetAllFilters}
                tooltip={
                  activeFilterCount > 0
                    ? `${t('Resetovať filtre')} (${filterCountLabel(activeFilterCount, t)})`
                    : t('Žiadne aktívne filtre')
                }
                icon={<CloseOutlined />}
                danger={activeFilterCount > 0}
                className={
                  activeFilterCount > 0
                    ? 'dataGridResetFilters dataGridResetFilters--active'
                    : 'dataGridResetFilters'
                }
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 32,
                  width: 32,
                  height: 32,
                  padding: 0,
                }}
              />
            </Badge>
          </div>
        ),
        width: columnWidth || undefined,
        render: (_, row) => {
          const renderCustomSettings =
            typeof customSettings === 'function' ? customSettings(row) : null;

          return (
            <div
              ref={columnRef}
              className="d-flex"
              onClick={(e) => e.stopPropagation()} /* action buttons must not trigger the row click */
            >
              {customSettingsPosition === 'before' ? renderCustomSettings : null}

              {onEditClick && (canUpdate || allowClick) ? (
                canUpdate && (secondaryCondition?.(row) ?? true) ? (
                  <IconButton
                    icon={editIcon || <EditOutlined />}
                    tooltip={editTooltip || t('Upraviť')}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditClick(row);
                    }}
                  />
                ) : (
                  <IconButton
                    icon={
                      editIcon ? React.cloneElement(editIcon, { style: { opacity: 0.35 } }) : <EditOutlined style={{ opacity: 0.35 }} />
                    }
                    tooltip={
                      typeof editDisabledTooltip === 'function'
                        ? editDisabledTooltip(row)
                        : editDisabledTooltip || t('Upraviť nie je dostupné')
                    }
                    disabledTooltip={
                      typeof editDisabledTooltip === 'function'
                        ? editDisabledTooltip(row)
                        : editDisabledTooltip || t('Upraviť nie je dostupné')
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    disabled
                  />
                )
              ) : null}

              {canShow && onPreviewClick && (
                <DataGridOverviewRowButton
                  row={row}
                  onPreviewClick={onPreviewClick}
                  previewTooltip={previewTooltip}
                />
              )}

              {(canDelete && onDelete) || (allowClick && onDelete) ? (
                deleteCondition?.(row) ? (
                  <IconButton
                    icon={deleteIcon || <DeleteOutlined />}
                    tooltip={deleteTooltip || t('Vymazať')}
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteModal ? setRowToBeDeleted(row) : onDelete(row);
                    }}
                  />
                ) : deleteDisabledTooltip ? (
                  <IconButton
                    icon={
                      deleteIcon
                        ? React.cloneElement(deleteIcon, { style: { opacity: 0.35 } })
                        : <DeleteOutlined style={{ opacity: 0.35 }} />
                    }
                    tooltip={
                      typeof deleteDisabledTooltip === 'function'
                        ? deleteDisabledTooltip(row)
                        : deleteDisabledTooltip
                    }
                    disabledTooltip={
                      typeof deleteDisabledTooltip === 'function'
                        ? deleteDisabledTooltip(row)
                        : deleteDisabledTooltip || t('Vymazať nie je dostupné')
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    disabled
                  />
                ) : null
              ) : null}

              {customSettingsPosition === 'after' ? renderCustomSettings : null}
            </div>
          );
        },
      });
    }
    return decoratedColumns;
  }, [
    columns,
    disableSettings,
    t,
    optionsWidth,
    columnWidth,
    customSettings,
    customSettingsPosition,
    onPreviewClick,
    onEditClick,
    showActionText,
    secondaryCondition,
    disableExpandRow,
    canUpdate,
    canDelete,
    onDelete,
    allowClick,
    resetAllFilters,
    activeFilterCount,
    token.colorWarning,
    openDeleteModal,
  ]);

  // !!! KĽÚČOVÁ ZMENA !!!
  // AntD odovzdáva filters pod column.key (ak existuje), inak pod dataIndex.
  // Preto si vždy vyrobíme identifikátor `key` cez getColumnKey.
  const dataGridColumns = useMemo(() => {
    return columnsWithSettings.map((columnDefinition) => {
      // eslint-disable-next-line no-unused-vars
      const { onFilterClear, filterValueFormatter, filterTitle, ...col } =
        columnDefinition;
      const key = getColumnKey(col);

      // Server-side filters keep their own filteredValue.
      if (isExternallyFilteredColumn(columnDefinition)) {
        return TABLE_STATES_CACHE &&
          col.sorter &&
          tableState.sorter.field === key
          ? { ...col, sortOrder: tableState.sorter.order }
          : col;
      }

      return TABLE_STATES_CACHE
        ? {
            ...col,
            filteredValue: tableState.filters[key] || null,
            ...(col.sorter && tableState.sorter.field === key
              ? { sortOrder: tableState.sorter.order }
              : {}),
          }
        : {
            ...col,
            filteredValue: filters[key] || null,
          };
    });
  }, [columnsWithSettings, filters, tableState]);

  useEffect(() => {
    const wrapper = tableWrapperRef.current;

    if (!wrapper) {
      return;
    }

    const focusableHeaderControls = wrapper.querySelectorAll(
      '.ant-dropdown-trigger.ant-table-filter-trigger, .ant-table-row',
    );

    focusableHeaderControls.forEach((control) => {
      control.setAttribute('tabindex', '0');
    });
  }, [dataSource, dataGridColumns]);

  const visibleRows = useMemo(() => {
    return dataSource.filter((row) => {
      // admin má všetko
      if (isAdmin) return true;

      // ak authorKey nie je definovaný, vráť všetko
      if (isUndefined(authorKey) || !(authorKey in row)) return true;

      // ak author je null, skryť
      if (isNull(row[authorKey])) return false;

      // inak len vlastné záznamy
      return row[authorKey] === user.id;
    });
  }, [authorKey, dataSource, isAdmin, user?.id]);

  const filteredRows = useMemo(() => {
    const rows = visibleRows;
    const entries = Object.entries(activeFilters || {});
    if (entries.length === 0) {
      return rows;
    }

    return rows.filter((row) =>
      entries.every(([columnKey, rawValue]) => {
        const column = dataGridColumns.find(
          (col) => getColumnKey(col) === columnKey,
        );

        if (!column || typeof column.onFilter !== 'function') {
          return true;
        }

        const filterValues = Array.isArray(rawValue) ? rawValue : [rawValue];
        const normalizedValues = filterValues.filter(
          (value) => value !== undefined && value !== null && value !== '',
        );

        if (normalizedValues.length === 0) {
          return true;
        }

        return normalizedValues.some((value) => column.onFilter(value, row));
      }),
    );
  }, [activeFilters, dataGridColumns, visibleRows]);

  const dynamicDeleteQuestion = useMemo(() => {
    const baseMessage = t(
      'Naozaj chcete vymazať túto položku? Položka bude trvalo odstránená. Operáciu nie je možné vrátiť späť!',
    );

    if (!rowToBeDeleted) return seriousDeleteQuestion ?? baseMessage;

    if (typeof seriousDeleteQuestion === 'function')
      return seriousDeleteQuestion(rowToBeDeleted);

    if (seriousDeleteQuestion) return seriousDeleteQuestion;

    return baseMessage;
  }, [rowToBeDeleted, seriousDeleteQuestion, t]);

  const pagination = useMemo(() => {
    const paginationProps = restProps.pagination || {};

    return {
      ...tableState.pagination,
      ...paginationProps,
      total:
        paginationProps.total !== undefined
          ? paginationProps.total
          : filteredRows.length,
      showTotal:
        paginationProps.showTotal ||
        ((total, range) =>
          `${range[0]}-${range[1]} ${t('z')} ${total}` +
          (activeFilterCount > 0 ? ` (${t('filtrované')})` : '')),
      showSizeChanger:
        paginationProps.showSizeChanger !== undefined
          ? paginationProps.showSizeChanger
          : true,
      responsive:
        paginationProps.responsive !== undefined
          ? paginationProps.responsive
          : true,
    };
  }, [
    filteredRows.length,
    restProps.pagination,
    tableState.pagination,
    activeFilterCount,
    t,
  ]);

  // Counts are meaningful only when filtering happens on the client.
  const isClientSideFiltering =
    !resetFiltersCallback && restProps.pagination?.total === undefined;

  const tableLocale = useMemo(() => {
    if (activeFilterCount === 0 || filteredRows.length > 0) {
      return restProps.locale;
    }

    return {
      ...restProps.locale,
      emptyText: (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('Žiadne záznamy nevyhovujú aktívnym filtrom')}
        >
          <Button
            type="primary"
            icon={<CloseOutlined />}
            onClick={resetAllFilters}
          >
            {t('Zrušiť filtre')}
          </Button>
        </Empty>
      ),
    };
  }, [
    activeFilterCount,
    filteredRows.length,
    restProps.locale,
    resetAllFilters,
    t,
  ]);

  return (
    <>
      <DataGridActiveFilters
        items={activeFilterItems}
        shownCount={isClientSideFiltering ? filteredRows.length : undefined}
        totalCount={isClientSideFiltering ? visibleRows.length : undefined}
        onClearAll={resetAllFilters}
      />
      <div
        ref={tableWrapperRef}
        className={`dataGridWrapper ${!filteredRows || filteredRows.length === 0 ? 'dataGridWrapper--empty' : ''}`}
      >
        <Table
          dataSource={filteredRows}
          showSorterTooltip={{
            color: '#fff0ca',
            styles: { body: { color: '#0e306d' } },
          }}
          rowStyle={{ height: 48 }}
          style={{
            width: '100%',
            ...(isRowClickable ? { cursor: 'pointer' } : {}),
          }}
          columns={dataGridColumns}
          expandable={{
            expandedRowRender: customExpandedRowRender,
            rowExpandable: (record) => {
              const disableRow = disableExpandRow(record);
              return customExpandedRowRender && !disableRow;
            },
            ...(defaultExpandedRowKeys ? { defaultExpandedRowKeys } : {}),
            ...(expandedRowKeys ? { expandedRowKeys } : {}),
            ...(onExpandedRowsChange ? { onExpandedRowsChange } : {}),
            ...(expandIcon ? { expandIcon } : {}),
          }}
          onRow={(record) => {
            return {
              onClick: (event) => {
                if (event.defaultPrevented) return;
                if (canUseRowClick) {
                  onRowClick(record);
                  return;
                }
                if (typeof onRowDetailClick === 'function') {
                  onRowDetailClick(record);
                  return;
                }
                if (canUsePreviewRowClick) {
                  onPreviewClick(record);
                }
              },
            };
          }}
          onChange={(pagination, filtersIn, sorter) => {
            // AntD filtersIn: { [column.key || dataIndex]: value[] | null }
            if (TABLE_STATES_CACHE) {
              dispatch(
                updateTableState({
                  tableId: tableId,
                  filters: filtersIn,
                  sorter: Array.isArray(sorter) ? sorter : { ...sorter },
                  pagination: {
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                  },
                }),
              );
            } else {
              setFilters(filtersIn);
            }
            onTableChange?.(pagination, filtersIn, sorter);
          }}
          ref={ref}
          rowSelection={rowSelection}
          {...restProps}
          locale={tableLocale}
          pagination={pagination}
        />
      </div>

      <QuestionModal
        chosenPositive={() => onDelete(rowToBeDeleted)}
        question={dynamicDeleteQuestion}
        visible={rowToBeDeleted !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeDeleted(null)}
        headerTitle={deleteModalTitle || t('Vymazať položku')}
      />
    </>
  );
});

DataGrid.propTypes = {
  data: PropTypes.array,
  secondaryCondition: PropTypes.func,
  deleteCondition: PropTypes.func,
  editDisabledTooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  editIcon: PropTypes.node,
  editTooltip: PropTypes.string,
  deleteIcon: PropTypes.node,
  deleteTooltip: PropTypes.string,
  deleteDisabledTooltip: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
  ]),
  onDelete: PropTypes.func,
  onPreviewClick: PropTypes.func,
  previewTooltip: PropTypes.string,
  onEditClick: PropTypes.func,
  onRowClick: PropTypes.func,
  onRowDetailClick: PropTypes.func,
  rowClickOpensPreview: PropTypes.bool,
  onChange: PropTypes.func,
  disableExpandRow: PropTypes.func,
  customSettings: PropTypes.any,
  customSettingsPosition: PropTypes.oneOf(['before', 'after']),
  permissionsBasic: PropTypes.string,
  resourceUrl: PropTypes.string,
  disableSettings: PropTypes.bool,
  optionsWidth: PropTypes.number,
  showActionText: PropTypes.bool,
  user: PropTypes.any,
  dispatchRedirectToPreview: PropTypes.func,
  dispatchRedirectToEdit: PropTypes.func,
  dispatchFetchData: PropTypes.func,
  dispatchUnload: PropTypes.func,
  allowClick: PropTypes.bool,
  openDeleteModal: PropTypes.bool,
  columns: PropTypes.array,
  dataSource: PropTypes.array,
  authorKey: PropTypes.string,
  seriousDeleteQuestion: PropTypes.node,
  pageSize: PropTypes.number,
  uniqueTableId: PropTypes.string,
  customExpandedRowRender: PropTypes.func,
  defaultExpandedRowKeys: PropTypes.array,
  expandedRowKeys: PropTypes.array,
  onExpandedRowsChange: PropTypes.func,
  rowSelection: PropTypes.object,
  expandIcon: PropTypes.func,
};

DataGrid.displayName = 'DataGrid';

export default DataGrid;
